/**
 * ThemeParks.wiki integration constants + the shape of the RTDB cache that the
 * `refreshwaittimes` / `refreshschedules` Cloud Functions write.
 */

/** Value stored on `Entity.externalSource` for ThemeParks.wiki-linked entities. */
export const THEMEPARKS_WIKI_SOURCE = "themeparks-wiki";

/** RTDB paths where the scheduled Cloud Functions write the cached live data. */
export const WIKI_LIVE_PATH = "themeparks_wiki/live";
export const WIKI_SCHEDULE_PATH = "themeparks_wiki/schedule";

/** Live wait data older than this (ms) is shown as stale. */
export const WIKI_LIVE_STALE_MS = 15 * 60 * 1000; // 15 minutes
/** Schedule data older than this (ms) is shown as possibly out of date. */
export const WIKI_SCHEDULE_STALE_MS = 24 * 60 * 60 * 1000; // 1 day

export interface WikiLocation {
  latitude: number;
  longitude: number;
}

export interface WikiShowtime {
  type?: string;
  startTime?: string;
  endTime?: string;
}

export interface WikiLiveEntity {
  /** Entity display name — carried in the cache mostly so the RTDB UI is readable. */
  name?: string;
  /** ThemeParks.wiki entityType (ATTRACTION / SHOW / RESTAURANT / …). */
  entityType?: string;
  /** Standby wait in minutes; absent when nothing is posted. */
  waitMinutes?: number;
  /** OPERATING / DOWN / CLOSED / REFURBISHMENT / UNKNOWN / … */
  status?: string;
  showtimes?: WikiShowtime[];
  /** Geo coordinates from ThemeParks.wiki, when available (feeds the "nearby" work). */
  location?: WikiLocation;
}

export interface WikiLivePark {
  fetchedAt?: number;
  parkName?: string;
  entities?: Record<string, WikiLiveEntity>;
}

export interface WikiScheduleDay {
  date: string; // YYYY-MM-DD
  type: string; // OPERATING / INFO / EXTRA_HOURS / …
  openingTime?: string; // ISO with offset
  closingTime?: string;
  description?: string;
}

export interface WikiSchedulePark {
  fetchedAt?: number;
  parkName?: string;
  timezone?: string;
  days?: WikiScheduleDay[];
}

/** True when an entity is linked to a ThemeParks.wiki entity. */
export function isWikiLinked(
  entity: { externalSource?: string | null; externalId?: string | null } | null | undefined,
): boolean {
  return (
    !!entity &&
    entity.externalSource === THEMEPARKS_WIKI_SOURCE &&
    typeof entity.externalId === "string" &&
    entity.externalId.length > 0
  );
}

/** Human-friendly label for a ThemeParks.wiki live status value. */
export function statusLabel(status: string | null | undefined): string | null {
  if (!status) return null;
  switch (status) {
    case "OPERATING":
      return "Operating";
    case "DOWN":
      return "Down";
    case "CLOSED":
      return "Closed";
    case "REFURBISHMENT":
      return "Refurbishment";
    case "UNKNOWN":
      return null;
    default:
      return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase().replace(/_/g, " ");
  }
}
